import { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import styled from 'foundations-theming/utils';
import { SprocketIcon } from 'foundations-assets/icons/components/SprocketIcon';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { useIsPartner } from '../../partners';
import UsageTracker from '../../../utils/UsageTracker';
import { PAGE_NAMES } from '../../../constants/Navigation';
import AcademyToSoftware from '../../../containers/AcademyToSoftware/AcademyToSoftware';
import { isHubSpotNativeApp } from '../../../utils/utils';
import { useNavigationState } from '../navbarQueries';
import { NAVBAR_ITEM_URLS } from '../navbarConstants';
import { MobileDropdown, MobileDropdownButton, MobileDropdownButtonItem, MobileDropdownLinkItem, useDropdownState } from './MobileDropdown';
import FullScreenModal from '../../../components/Modals/FullScreenModal/FullScreenModal';
import { NavbarSavedItemsTooltip } from '../../savedItems';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ClickCatcherContainer = styled.div.withConfig({
  displayName: "NavbarMobileTabs__ClickCatcherContainer",
  componentId: "sc-18925bl-0"
})(["opacity:0;"]);
const MobileDropdownRowWrapper = ({
  children
}) => /*#__PURE__*/_jsx(View, {
  borderBlockEndColor: "do-not-use-heffalump",
  borderBlockEndStyle: "solid",
  borderBlockEndWidth: "100",
  inlineSize: "100%",
  marginInlineStart: "600",
  paddingBlock: "300",
  children: children
});
const MobileDropdownMenu = ({
  'data-test-id': dataTestId,
  children,
  onClose
}) => {
  const {
    toggle,
    show
  } = useDropdownState();
  const handleClose = useCallback(() => {
    if (show) {
      toggle();
      onClose();
    }
  }, [onClose, show, toggle]);
  return /*#__PURE__*/_jsxs("nav", {
    "aria-hidden": !show,
    children: [show && /*#__PURE__*/_jsx(View, {
      insetBlockStart: "0",
      insetInlineEnd: "0",
      insetInlineStart: "0",
      position: "fixed",
      zIndex: 0,
      children: /*#__PURE__*/_jsx(ClickCatcherContainer, {
        children: /*#__PURE__*/_jsx(Button, {
          "aria-label": I18n.text('academyNav.close'),
          "data-test-id": "mobile-dropdown-close-button",
          onClick: handleClose
        })
      })
    }), /*#__PURE__*/_jsx(FullScreenModal, {
      backgroundColor: "do-not-use-flat-earth",
      "data-test-id": dataTestId,
      marginTop: "44px",
      open: show,
      overflow: "hidden",
      showCloseButton: false,
      children: /*#__PURE__*/_jsx(Flex, {
        alignItems: "start",
        flexDirection: "column",
        justifyContent: "start",
        children: children
      })
    })]
  });
};
const handleDropdownOpen = () => {
  UsageTracker.track('interaction', {
    action: 'mobile-nav-dropdown-open'
  });
};
const handleDropdownClose = () => {
  UsageTracker.track('interaction', {
    action: 'mobile-nav-dropdown-close'
  });
};
const handleDropdownItemClick = urlLocation => () => {
  UsageTracker.track('interaction', {
    action: 'mobile-nav-dropdown-item-clicked',
    item: urlLocation
  });
};
const handleAcademyToSoftwareClick = () => {
  UsageTracker.track('interaction', {
    action: 'mobile-nav-dropdown-academy-to-software-click'
  });
};
const getPageI18nKey = pageName => {
  const i18nKeys = {
    [PAGE_NAMES.CATALOG]: 'academyNav.heading.mobileNavLinkTitles.tracks',
    [PAGE_NAMES.MY_LEARNING]: 'academyNav.heading.mobileNavLinkTitles.mylearning',
    [PAGE_NAMES.WATCH_LATER]: 'academyNav.heading.watchLater',
    [PAGE_NAMES.PLAYLISTS]: 'academyNav.heading.myPlaylists',
    [PAGE_NAMES.CREATE_PLAYLIST]: 'academyNav.heading.createPlaylist',
    [PAGE_NAMES.TEAM]: 'academyNav.heading.mobileNavLinkTitles.team',
    [PAGE_NAMES.PARTNER]: 'academyNav.heading.mobileNavLinkTitles.partners'
  };
  if (!pageName) return i18nKeys[PAGE_NAMES.CATALOG];
  return i18nKeys[pageName] || i18nKeys[PAGE_NAMES.CATALOG];
};
const NavbarMobileTabs = () => {
  const isPartner = useIsPartner();
  const history = useHistory();
  const {
    backLink,
    previousPageName,
    activePageName,
    nonNavPageSelected
  } = useNavigationState();
  const getPageUrl = pageName => {
    if (nonNavPageSelected && pageName === previousPageName) {
      return backLink;
    }
    return NAVBAR_ITEM_URLS[pageName];
  };
  useEffect(() => {
    UsageTracker.track('interaction', {
      action: 'mobile-nav-viewed'
    });
  }, []);
  const handleSprocketClick = useCallback(() => {
    history.push('/');
    UsageTracker.track('interaction', {
      action: 'mobile-nav-sprocket-clicked'
    });
  }, [history]);
  return /*#__PURE__*/_jsx(NavbarSavedItemsTooltip, {
    children: /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      paddingInlineStart: "400",
      children: [/*#__PURE__*/_jsx(IconButton, {
        "aria-label": I18n.text(getPageI18nKey(PAGE_NAMES.CATALOG)),
        "data-onboarding": "academy-logo",
        "data-test-id": "mobile-sprocket-home-button",
        onClick: handleSprocketClick,
        size: "default",
        use: "unstyled",
        children: /*#__PURE__*/_jsx(IconHolder, {
          color: "fill-brand-default",
          icon: SprocketIcon,
          size: "800"
        })
      }), /*#__PURE__*/_jsxs(MobileDropdown, {
        children: [/*#__PURE__*/_jsx(MobileDropdownButton, {
          "data-test-id": "mobile-dropdown-open-button",
          onClick: handleDropdownOpen,
          children: /*#__PURE__*/_jsx(Text, {
            color: "text-core-on-fill-default",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getPageI18nKey(activePageName)
            })
          })
        }), /*#__PURE__*/_jsxs(MobileDropdownMenu, {
          "data-test-id": "mobile-dropdown-menu",
          onClose: handleDropdownClose,
          children: [/*#__PURE__*/_jsx(MobileDropdownRowWrapper, {
            children: /*#__PURE__*/_jsx(MobileDropdownLinkItem, {
              "data-test-id": `mobile-dropdown-item-${PAGE_NAMES.CATALOG}`,
              onClick: handleDropdownItemClick(PAGE_NAMES.CATALOG),
              to: getPageUrl(PAGE_NAMES.CATALOG),
              children: /*#__PURE__*/_jsx(Text, {
                color: "text-interactive-on-fill-default",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: getPageI18nKey(PAGE_NAMES.CATALOG)
                })
              })
            }, PAGE_NAMES.CATALOG)
          }), /*#__PURE__*/_jsx(MobileDropdownRowWrapper, {
            children: /*#__PURE__*/_jsx(MobileDropdownLinkItem, {
              "data-test-id": `mobile-dropdown-item-${PAGE_NAMES.MY_LEARNING}`,
              onClick: handleDropdownItemClick(PAGE_NAMES.MY_LEARNING),
              to: getPageUrl(PAGE_NAMES.MY_LEARNING),
              children: /*#__PURE__*/_jsx(Text, {
                color: "text-interactive-on-fill-default",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: getPageI18nKey(PAGE_NAMES.MY_LEARNING)
                })
              })
            }, PAGE_NAMES.MY_LEARNING)
          }), /*#__PURE__*/_jsx(MobileDropdownRowWrapper, {
            children: /*#__PURE__*/_jsx(MobileDropdownLinkItem, {
              "data-test-id": `mobile-dropdown-item-${PAGE_NAMES.WATCH_LATER}`,
              onClick: handleDropdownItemClick(PAGE_NAMES.WATCH_LATER),
              to: getPageUrl(PAGE_NAMES.WATCH_LATER),
              children: /*#__PURE__*/_jsx(Text, {
                color: "text-interactive-on-fill-default",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: getPageI18nKey(PAGE_NAMES.WATCH_LATER)
                })
              })
            }, PAGE_NAMES.WATCH_LATER)
          }), /*#__PURE__*/_jsx(MobileDropdownRowWrapper, {
            children: /*#__PURE__*/_jsx(MobileDropdownLinkItem, {
              "data-test-id": `mobile-dropdown-item-${PAGE_NAMES.PLAYLISTS}`,
              onClick: handleDropdownItemClick(PAGE_NAMES.PLAYLISTS),
              to: getPageUrl(PAGE_NAMES.PLAYLISTS),
              children: /*#__PURE__*/_jsx(Text, {
                color: "text-interactive-on-fill-default",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: getPageI18nKey(PAGE_NAMES.PLAYLISTS)
                })
              })
            }, PAGE_NAMES.PLAYLISTS)
          }), /*#__PURE__*/_jsx(MobileDropdownRowWrapper, {
            children: /*#__PURE__*/_jsx(MobileDropdownLinkItem, {
              "data-test-id": `mobile-dropdown-item-${PAGE_NAMES.MY_PROFILE}`,
              onClick: handleDropdownItemClick(PAGE_NAMES.MY_PROFILE),
              to: getPageUrl(PAGE_NAMES.MY_PROFILE),
              children: /*#__PURE__*/_jsx(Text, {
                color: "text-interactive-on-fill-default",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "academyNav.heading.myProfile"
                })
              })
            }, PAGE_NAMES.MY_PROFILE)
          }), isPartner && /*#__PURE__*/_jsx(MobileDropdownRowWrapper, {
            children: /*#__PURE__*/_jsx(MobileDropdownLinkItem, {
              "data-test-id": `mobile-dropdown-item-${PAGE_NAMES.PARTNER}`,
              onClick: handleDropdownItemClick(PAGE_NAMES.PARTNER),
              to: getPageUrl(PAGE_NAMES.PARTNER),
              children: /*#__PURE__*/_jsx(Text, {
                color: "text-interactive-on-fill-default",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: getPageI18nKey(PAGE_NAMES.PARTNER)
                })
              })
            }, PAGE_NAMES.PARTNER)
          }), !isHubSpotNativeApp && /*#__PURE__*/_jsx(MobileDropdownRowWrapper, {
            children: /*#__PURE__*/_jsx(MobileDropdownButtonItem, {
              "data-test-id": "mobile-dropdown-item-academy-to-software",
              onClick: handleAcademyToSoftwareClick,
              children: /*#__PURE__*/_jsx(Text, {
                color: "text-interactive-on-fill-default",
                children: /*#__PURE__*/_jsx(AcademyToSoftware, {
                  use: "inline"
                })
              })
            })
          })]
        })]
      })]
    })
  });
};
export default NavbarMobileTabs;